import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { ETransportType } from '../../../common/types';
import { CustomH3, HTMLText } from '../../../components/SEO';
import {
  formatDuration,
  formatRoundPrice,
  getRoundPrice,
} from '../../../helpers/results';
import { City } from '../../../services/cities/types';
import { CompaniesData, CompanyData } from '../../../services/data/types';
import Accordion from '../../HomePage/View/FAQ/Accordion';
import {
  SPECIFIC_COMPANIES_TEXT_HEADERS,
  SPECIFIC_COMPANIES_TEXTS,
  TRANSPORT_MEANS_EMOJIS,
} from '../constants';
import { generateSeed } from '../helpers/TextGen';
import { GetFormattedTripMessagesResponse } from '../types';
import { getFormattedTripMessages } from '../utils';
import messages from './messages';
import { SplitContentLayout } from './SplitCotentLayout';

const Container = styled.div``;

type SpecificCompaniesTextProps = {
  companiesData: CompaniesData;
  pageTransportType: ETransportType | null;
  originCity: City;
  destinationCity: City;
  intl: any;
};

const SpecificCompaniesText = (props: SpecificCompaniesTextProps) => {
  const {
    companiesData,
    destinationCity,
    intl,
    originCity,
    pageTransportType,
  } = props;

  if (
    !companiesData ||
    !pageTransportType ||
    pageTransportType === ETransportType.CARPOOLING
  ) {
    return null;
  }

  const availableCompanies = Object.keys(companiesData);
  const formattedTripMessages = getFormattedTripMessages(
    intl,
    originCity,
    destinationCity,
  );
  const pageSeed = generateSeed(window.location.pathname);

  return (
    <Container>
      <SplitContentLayout>
        {availableCompanies.map((company, index) => (
          <SpecificCompanyText
            key={index}
            companyData={companiesData[company]}
            companyName={company}
            destinationCity={destinationCity}
            formattedTripMessages={formattedTripMessages}
            originCity={originCity}
            seed={pageSeed + index}
            transportType={pageTransportType}
          />
        ))}
      </SplitContentLayout>
    </Container>
  );
};

export const SpecificCompaniesTextIntl = injectIntl(SpecificCompaniesText);

type SpecificCompanyTextProps = {
  companyData: CompanyData;
  companyName: string;
  transportType: ETransportType;
  formattedTripMessages: GetFormattedTripMessagesResponse;
  seed: number;
  originCity: City;
  destinationCity: City;
};

const SpecificCompanyText = (props: SpecificCompanyTextProps) => {
  const {
    companyData,
    companyName,
    destinationCity,
    formattedTripMessages,
    originCity,
    seed,
    transportType,
  } = props;

  const messageValues = {
    companyName,
    formattedTrip1: formattedTripMessages.formattedTrip1Message,
    formattedTrip2: formattedTripMessages.formattedTrip2Message,
    formattedTrip3: formattedTripMessages.formattedTrip3Message,
    emoji: TRANSPORT_MEANS_EMOJIS[transportType],
    origin: originCity.name,
    destination: destinationCity.name,
    transportType,
    firstDepartureTime: companyData.firstTrip.departureTime,
    lastDepartureTime: companyData.lastTrip.departureTime,
    minPrice: formatRoundPrice(getRoundPrice(companyData.priceCents.min)),
    maxPrice: formatRoundPrice(getRoundPrice(companyData.priceCents.max)),
    meanPrice: formatRoundPrice(getRoundPrice(companyData.priceCents.mean)),
    minDuration: formatDuration(companyData.durationMinutes.min),
    maxDuration: formatDuration(companyData.durationMinutes.max),
    meanDuration: formatDuration(companyData.durationMinutes.mean),
    originStations: companyData.stations.origins
      .map((station) => station.name)
      .join(', '),
    destinationStations: companyData.stations.destinations
      .map((station) => station.name)
      .join(', '),
    dailyTrips: companyData.dailyTrips,
  };

  return (
    <Accordion
      header={
        <CustomH3>
          <FormattedHTMLMessage
            {...messages[
              SPECIFIC_COMPANIES_TEXT_HEADERS[
                seed % SPECIFIC_COMPANIES_TEXT_HEADERS.length
              ]
            ]}
            values={messageValues}
          />
        </CustomH3>
      }
      isOpenByDefault
    >
      <HTMLText
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {SPECIFIC_COMPANIES_TEXTS[seed % SPECIFIC_COMPANIES_TEXTS.length].map(
          (specificMessages) => (
            <FormattedHTMLMessage
              {...messages[specificMessages]}
              values={messageValues}
            />
          ),
        )}
      </HTMLText>
    </Accordion>
  );
};
